/**
 * /campaigns/:slug — Public landing page that drops external visitors directly
 * into the Quick Order flow without requiring sign-in upfront.
 *
 * Login wall is shown ONLY when the visitor clicks "Continue with this package".
 * Selected campaign + bundle + qty + title + price is then persisted to
 * localStorage and redeemed by AuthContext after a successful login.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Loader2, ArrowRight, ShieldCheck, Mail } from 'lucide-react';
import QuickOrderFlow from '../components/QuickOrderFlow';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { API_BASE } from '../lib/apiBase';
import { resolvePublicCurrency } from '../lib/publicCurrency';
import { trackMarketingVisit } from '../lib/marketingVisit';
import { usePublicTheme } from '../hooks/usePublicTheme';
import '@owntune/theme/theme.css';
import '@owntune/ui/styles.css';
import { PublicUIProvider } from '@owntune/ui';

const API = `${API_BASE}/api`;
const PENDING_DRAFT_KEY = 'pending_campaign_draft';
// Currency handoff is now shared with `/l/{slug}` — see
// `lib/publicCurrency.js`. Extending the supported list to include
// USD/GBP is safe because previously stored EUR/DKK/SEK/NOK values
// remain valid.

export default function CampaignLandingPage() {
    const { t, language, applyEntryPointDefault } = useTranslation();
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login, user } = useAuth();

    // Apply ?theme=dark|light (falling back to prefers-color-scheme)
    // so the anonymous funnel matches the embedding marketing site.
    usePublicTheme();

    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Login wall state
    const [pendingPayload, setPendingPayload] = useState(null);
    const [authOpen, setAuthOpen] = useState(false);
    const [authMode, setAuthMode] = useState('register'); // 'register' | 'login'
    const [authEmail, setAuthEmail] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [authName, setAuthName] = useState('');
    const [authBusy, setAuthBusy] = useState(false);

    // Magic Link state (mirrors LoginPage)
    const [magicLinkEmail, setMagicLinkEmail] = useState('');
    const [magicLinkSent, setMagicLinkSent] = useState(false);
    const [magicLinkLoading, setMagicLinkLoading] = useState(false);
    const [showMagicLink, setShowMagicLink] = useState(false);

    // Currency handoff from Lovable marketing site (?currency=DKK, etc.).
    // Resolved once per slug+query change. A valid value also persists to
    // localStorage so it survives reload + post-login redirect.
    const campaignCurrency = useMemo(
        () => resolvePublicCurrency(searchParams.get('currency')),
        [searchParams],
    );

    // Fetch campaign — re-runs on language change so bundles and
    // description swap in-place without reload.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const langQ = language && language !== 'en' ? `?lang=${encodeURIComponent(language)}` : '';
                const res = await axios.get(`${API}/public/campaigns/${slug}${langQ}`);
                if (!cancelled) {
                    setCampaign(res.data);
                    // Apply Campaign default_language (priority 4). No-op if
                    // the visitor already has priority 1/2/3 satisfied.
                    if (res.data?.default_language) {
                        applyEntryPointDefault(res.data.default_language);
                    }
                }
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.detail || t('campaign.not_found', 'Campaign not found'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug, language]);

    // Marketing analytics — record a visit exactly once per session
    // per slug. Fire-and-forget; ignores auth-header response so it
    // works for anonymous visitors.
    useEffect(() => {
        if (slug) trackMarketingVisit('campaign', slug);
    }, [slug]);

    // If user lands here while already authed AND has a pending draft from a
    // prior session, redeem it directly (covers the "logged in already" case).
    useEffect(() => {
        if (!user) return;
        const raw = localStorage.getItem(PENDING_DRAFT_KEY);
        if (!raw) return;
        try {
            const draft = JSON.parse(raw);
            redeemDraft(draft);
        } catch {
            localStorage.removeItem(PENDING_DRAFT_KEY);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleQuickOrderComplete = (specs, priceEstimate, meta) => {
        const draft = {
            campaign_slug: campaign.slug,
            campaign_id: campaign.id,
            product_family: campaign.product_family,
            bundle_id: meta?.bundle?.id,
            bundle_title: meta?.bundle?.title,
            quantity: meta?.quantity,
            title: meta?.title || campaign.title,
            specs,
            price_estimate: priceEstimate,
            quote_comment: meta?.quote_comment || null,
            created_at: new Date().toISOString(),
        };
        localStorage.setItem(PENDING_DRAFT_KEY, JSON.stringify(draft));
        setPendingPayload(draft);

        if (user) {
            // Already authed — skip login wall, go straight to order create
            redeemDraft(draft);
        } else {
            setAuthOpen(true);
        }
    };

    const redeemDraft = async (draft) => {
        try {
            // Build vinyl_specs from the bundle specs the customer selected on
            // the campaign page. We pass through ALL bundle-supplied options
            // (test_pressings, sleeve, inner_sleeve, finishing, etc.) instead
            // of hardcoding a generic set — otherwise the next page shows
            // generic defaults and the bundle's price can't be recomputed
            // because `_bundle_id` is missing from vinyl_specs.
            const specs = draft.specs || {};
            // Capture the quote-gate hint so we can branch the submit
            // path to the dedicated request-quote endpoint instead of
            // creating a bogus Draft order with zero totals.
            const requiresQuote = !!specs._requires_quote;
            const weightRaw = specs.vinyl_weight ?? specs.vinyl_weight_g ?? '140';
            const weightInt = parseInt(String(weightRaw).replace('g', ''), 10) || 140;

            let vinyl_specs = null;
            if (draft.product_family === 'vinyl') {
                vinyl_specs = {
                    // Spread ALL bundled options first so sleeve/inner_sleeve/
                    // test_pressings/finishing etc. are preserved.
                    ...specs,
                    // Required typed fields — coerce/override the spread values
                    // so the backend Pydantic model validates cleanly.
                    format: specs.format || '12"',
                    quantity: draft.quantity,
                    speed: specs.speed || '33 RPM',
                    color: specs.color || 'Black',
                    vinyl_weight_g: weightInt,
                    test_pressings: parseInt(String(
                        specs.test_pressings ?? 0
                    ), 10) || 0,
                    // Bundle metadata MUST live inside vinyl_specs — the
                    // `/api/sales/orders/{id}/price` endpoint reads
                    // `vinyl_specs._bundle_id` to recompute pricing.
                    _bundle_id: draft.bundle_id,
                    _bundle_title: draft.bundle_title,
                };
                // Drop client-only hint fields.
                delete vinyl_specs._requires_quote;
                delete vinyl_specs._quote_gated_skus;
            }

            const body = {
                title: draft.title,
                product_type: draft.product_family || 'vinyl',
                quantity: draft.quantity,
                vinyl_specs,
                order_type: 'new_production',
            };
            const token = localStorage.getItem('token');
            if (requiresQuote) {
                const comment = (draft.quote_comment || '').trim();
                const res = await axios.post(
                    `${API}/orders/request-quote`,
                    { order: body, comment: comment || null },
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                localStorage.removeItem(PENDING_DRAFT_KEY);
                toast.success(`Quote requested — order ${res.data.order_number}`);
                navigate(`/orders/${res.data.id}`);
                return;
            }
            const res = await axios.post(`${API}/orders`, body, {
                headers: { Authorization: `Bearer ${token}` },
            });
            localStorage.removeItem(PENDING_DRAFT_KEY);
            toast.success(`Order ${res.data.order_number} created from campaign`);
            navigate(`/orders/${res.data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create order after login');
        }
    };

    const handleAuthSubmit = async () => {
        if (!authEmail || !authPassword) {
            toast.error(t('auth.fill_email_password', 'Please fill in your email and password'));
            return;
        }
        setAuthBusy(true);
        try {
            if (authMode === 'register') {
                if (!authName.trim()) {
                    toast.error(t('auth.enter_name', 'Please enter your name'));
                    setAuthBusy(false);
                    return;
                }
                await axios.post(`${API}/auth/register`, {
                    email: authEmail,
                    password: authPassword,
                    name: authName.trim(),
                });
            }
            await login(authEmail, authPassword);
            setAuthOpen(false);
            // useEffect on user will trigger redeemDraft
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Authentication failed');
        } finally {
            setAuthBusy(false);
        }
    };

    const handleMagicLink = async (e) => {
        e.preventDefault();
        if (!magicLinkEmail.trim()) return;
        setMagicLinkLoading(true);
        try {
            await axios.post(`${API}/auth/magic-link/request`, { email: magicLinkEmail.trim() });
            setMagicLinkSent(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send magic link');
        } finally {
            setMagicLinkLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6">
                <Card className="max-w-md w-full">
                    <CardContent className="py-10 text-center space-y-3">
                        <h1 className="text-xl font-bold text-foreground">{t('campaign.not_found', 'Campaign not found')}</h1>
                        <p className="text-sm text-muted-foreground">{error}</p>
                        <Button onClick={() => navigate('/login')} variant="outline">{t('common.go_to_signin', 'Go to sign in')}</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!campaign) return null;

    // Determine effective bundle + starting step:
    // - URL ?bundle=xxx ALWAYS wins → start at quantity step
    // - Else if campaign.starting_step == 'quantity':
    //     use default_bundle_id, or fall back to the first bundle in the list
    // - Else → start at packages
    const urlBundleId = searchParams.get('bundle');
    let effectiveBundleId = null;
    let effectiveStartingStep = 'packages';

    if (urlBundleId) {
        effectiveBundleId = urlBundleId;
        effectiveStartingStep = 'quantity';
    } else if (campaign.starting_step === 'quantity') {
        effectiveBundleId =
            campaign.default_bundle_id ||
            (campaign.bundles && campaign.bundles[0]?.id) ||
            null;
        effectiveStartingStep = effectiveBundleId ? 'quantity' : 'packages';
    }

    return (
        <PublicUIProvider active>
        <div className="min-h-screen bg-background" data-testid="campaign-landing">
            <main className="max-w-none mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-10 space-y-4">
                <div className="text-center max-w-3xl mx-auto">
                    <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                        {campaign.title}
                    </h1>
                    {campaign.description && (
                        <p className="text-sm text-muted-foreground mt-1.5">{campaign.description}</p>
                    )}
                </div>

                <QuickOrderFlow
                    bundles={campaign.bundles}
                    productFamily={campaign.product_family}
                    initialBundleId={effectiveBundleId}
                    startingStep={effectiveStartingStep}
                    requireTitle={true}
                    initialTitle=""
                    onComplete={handleQuickOrderComplete}
                    submitLabel={t('campaign.continue_with_package', 'Continue with this package')}
                    compact={true}
                    currencyOverride={campaignCurrency}
                />

                <p className="text-[11px] text-center text-muted-foreground pt-1">
                    {t('campaign.powered_footer', 'Powered by Owntune · No commitment required to see pricing')}
                </p>
            </main>

            {/* Login wall modal */}
            <Dialog open={authOpen} onOpenChange={setAuthOpen}>
                <DialogContent className="max-w-md" data-testid="campaign-auth-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 font-display">
                            <ShieldCheck className="h-5 w-5 text-amber-500" />
                            {t('campaign.auth_title', 'Create your production workspace to continue')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('campaign.auth_desc', 'Your {bundle} ({qty} copies) campaign is ready. Register to complete and claim your special offer.', { bundle: pendingPayload?.bundle_title || '', qty: pendingPayload?.quantity || 0 })}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Mode toggle */}
                    <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                        <button
                            onClick={() => setAuthMode('register')}
                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                                authMode === 'register' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600'
                            }`}
                            data-testid="campaign-auth-mode-register"
                        >
                            {t('landing.new_here', 'New here')}
                        </button>
                        <button
                            onClick={() => setAuthMode('login')}
                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                                authMode === 'login' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600'
                            }`}
                            data-testid="campaign-auth-mode-login"
                        >
                            {t('landing.returning_customer', 'Returning customer')}
                        </button>
                    </div>

                    <div className="space-y-3 py-2">
                        {authMode === 'register' && (
                            <div>
                                <Label htmlFor="auth-name">{t('auth.your_name_lower', 'Your name')}</Label>
                                <Input
                                    id="auth-name"
                                    value={authName}
                                    onChange={(e) => setAuthName(e.target.value)}
                                    placeholder={t('auth.full_name_placeholder', 'Full name')}
                                    className="mt-1"
                                    data-testid="campaign-auth-name"
                                />
                            </div>
                        )}
                        <div>
                            <Label htmlFor="auth-email">{t('auth.email_field', 'Email')}</Label>
                            <Input
                                id="auth-email"
                                type="email"
                                value={authEmail}
                                onChange={(e) => setAuthEmail(e.target.value)}
                                placeholder={t('auth.email_label_at_placeholder', 'you@label.com')}
                                className="mt-1"
                                data-testid="campaign-auth-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="auth-pwd">{t('common.password', 'Password')}</Label>
                            <Input
                                id="auth-pwd"
                                type="password"
                                value={authPassword}
                                onChange={(e) => setAuthPassword(e.target.value)}
                                placeholder={authMode === 'register' ? t('auth.create_password_placeholder', 'Create a password') : t('auth.your_password_placeholder', 'Your password')}
                                className="mt-1"
                                data-testid="campaign-auth-password"
                            />
                        </div>

                        <Button
                            onClick={handleAuthSubmit}
                            disabled={authBusy}
                            className="w-full text-white border-0 shadow-md hover:shadow-lg hover:brightness-105 transition-all"
                            style={{ backgroundImage: 'linear-gradient(135deg, hsl(35 92% 45%), hsl(42 96% 55%))' }}
                            data-testid="campaign-auth-submit"
                        >
                            {authBusy ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <ArrowRight className="h-4 w-4 mr-2" />
                            )}
                            {authMode === 'register' ? t('campaign.create_workspace_continue', 'Create workspace & continue') : t('landing.signin_and_continue', 'Sign in & continue')}
                        </Button>

                        <p className="text-xs text-center text-slate-400">
                            {t('campaign.terms_notice', "By continuing you agree to Owntune's terms. Your draft is stored only on this device until login.")}
                        </p>

                        {/* OR divider */}
                        <div className="relative my-2">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-slate-200" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-white px-3 text-slate-400 font-medium">{t('common.or', 'OR')}</span>
                            </div>
                        </div>

                        {/* Continue with Google */}
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full h-11 font-medium border-slate-300 hover:bg-slate-50 text-slate-700"
                            data-testid="campaign-google-login-button"
                            onClick={() => {
                                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                const redirectUrl = window.location.href;
                                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                            }}
                        >
                            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            {t('auth.continue_google', 'Continue with Google')}
                        </Button>

                        {/* Magic Link */}
                        {!showMagicLink ? (
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-11 font-medium border-slate-300 hover:bg-slate-50 text-slate-700"
                                data-testid="campaign-magic-link-toggle-button"
                                onClick={() => setShowMagicLink(true)}
                            >
                                <Mail className="mr-2 h-4 w-4" />
                                {t('auth.magic_link_toggle', 'Sign in with email link')}
                            </Button>
                        ) : magicLinkSent ? (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center" data-testid="campaign-magic-link-sent">
                                <Mail className="h-6 w-6 text-green-600 mx-auto mb-2" />
                                <p className="text-sm font-medium text-green-800">{t('auth.check_inbox', 'Check your inbox!')}</p>
                                <p className="text-xs text-green-600 mt-1">{t('auth.magic_link_sent_to', 'We sent a login link to {email}', { email: magicLinkEmail })}</p>
                                <button
                                    className="text-xs text-blue-600 hover:underline mt-2"
                                    onClick={() => { setMagicLinkSent(false); setShowMagicLink(false); }}
                                >
                                    {t('auth.use_different_method', 'Use a different method')}
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleMagicLink} className="space-y-2" data-testid="campaign-magic-link-form">
                                <Input
                                    type="email"
                                    placeholder={t('auth.email_placeholder_generic', 'your@email.com')}
                                    value={magicLinkEmail}
                                    onChange={(e) => setMagicLinkEmail(e.target.value)}
                                    required
                                    className="h-10"
                                    data-testid="campaign-magic-link-email-input"
                                    autoFocus
                                />
                                <div className="flex gap-2">
                                    <Button
                                        type="submit"
                                        className="flex-1 h-9 bg-slate-900 hover:bg-slate-800 text-white text-sm"
                                        disabled={magicLinkLoading}
                                        data-testid="campaign-magic-link-submit-button"
                                    >
                                        {magicLinkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('auth.send_login_link', 'Send login link')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        className="h-9 text-sm px-3"
                                        onClick={() => setShowMagicLink(false)}
                                    >
                                        {t('common.cancel', 'Cancel')}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
        </PublicUIProvider>
    );
}
